import type { AuditDetail, AuditEntry, AuditList } from "@closer/shared";
import { ENTERPRISE_TENANT_TYPES } from "@closer/shared";
import { Badge, Button, Card, FormField, Input, PageHeader, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDateTime } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { AiOpsInsightSection } from "./AiOpsInsightSection";
import { AuditExportPanel } from "./AuditExportPanel";

/**
 * AUDIT KAYITLARI (CLOS-193 `[171]`).
 *
 * ### ⚠ BU EKRAN HİÇBİR ŞEY DEĞİŞTİRMİYOR
 *
 * Kartın kriteri: *"Düzenleme, silme, gizleme, geri yükleme veya audit olayından domain
 * işlemini geri alma aksiyonu bulunmaz."* Dosyada tek bir `useMutation` yok ve olmaması
 * yapısal — audit değişmez bir kanıt defteri, düzenlenebilir bir liste değil.
 *
 * ### ⚠ HEDEF BAĞLANTISI SUNUCUNUN KARARI
 *
 * Kartın kriteri: *"Audit görüntüleme permission'ı hedef kaydın içeriğini görme hakkı
 * değildir; kayıt erişimi yoksa hedef bağlantısı kapanır."* Karar `targetAccessible` ile
 * geliyor; istemci kendi başına "bu kayda gidebilir miyim" diye tahmin etmiyor. Tahmin
 * etseydik, iki yerde iki farklı cevap doğar ve biri yanlış olurdu.
 *
 * ### ⚠ SATIR DÜŞMÜYOR, BAĞLANTISI KAPANIYOR
 *
 * Erişilemeyen hedefin satırı listede DURUYOR — bildirimden farklı bir karar. Bildirimde
 * satır bir haberdir ve erişilemeyen kaydın haberi kaydın varlığını ele verir; audit'te
 * ise "kim ne yaptı" bilgisinin kendisi kartın konusu. Satırı düşürseydik audit eksik bir
 * kanıt defteri olurdu.
 */
export function AuditPage() {
  const { activeTenantId, activeTenant, can } = useAuth();
  const { preferences } = usePreferences();

  const [kategori, setKategori] = useState("");
  /**
   * ⚠ Aktör süzgeci KİMLİK + AD çifti tutuyor.
   *
   * Yalnız kimliği tutsaydık kutuda bir UUID görünürdü ve kullanıcı kimi süzdüğünü
   * okuyamazdı; yalnız adı tutsaydık sunucuya gidecek değer olmazdı (adlar benzersiz
   * değil). İkisi ayrı: biri gösterilen, öteki gönderilen.
   */
  const [aktor, setAktor] = useState<{ id: string; ad: string } | null>(null);
  const [correlation, setCorrelation] = useState("");
  const [baslangic, setBaslangic] = useState("");
  const [secili, setSecili] = useState<string | null>(null);
  /** Denetim kaydı ilk açılışta listeyi gösterir; ayrıntılı daraltma isteğe bağlıdır. */
  const [suzgeclerAcik, setSuzgeclerAcik] = useState(false);

  const sorguDizesi = new URLSearchParams();
  if (kategori) sorguDizesi.set("category", kategori);
  if (aktor) sorguDizesi.set("actorId", aktor.id);
  if (correlation) sorguDizesi.set("correlationId", correlation);
  if (baslangic) sorguDizesi.set("from", new Date(baslangic).toISOString());
  const ek = sorguDizesi.toString();

  const sorgu = useQuery({
    queryKey: activeTenantId ? tenantKey(activeTenantId, "audit", ek) : ["t", "yok", "audit", ek],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<AuditList>(`/api/audit${ek ? `?${ek}` : ""}`, {
          tenantId: activeTenantId!,
        })
      ).data,
  });

  const detay = useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "audit-detay", secili ?? "")
      : ["t", "yok", "audit-detay", secili ?? ""],
    enabled: activeTenantId !== null && secili !== null,
    queryFn: async () =>
      (await api<AuditDetail>(`/api/audit/${secili!}`, { tenantId: activeTenantId! })).data,
  });

  /**
   * Hata sunumu ORTAK KAPIDAN. 403 burada "bu ekranı görme yetkiniz yok" demek — audit
   * yetkisi ayrı bir anahtar ve çoğu kullanıcıda yok.
   */
  /**
   * ⚠ RET SEBEBİ ÇALIŞMA ALANI TÜRÜNE GÖRE DEĞİŞİYOR.
   *
   * Canlı turda görüldü: kişisel alanda ekran *"organizasyon yöneticinizle görüşün"*
   * diyordu — orada yönetici YOK. Metin kullanıcıyı var olmayan birine yolluyordu.
   *
   * Tür bilgisi bir yetki haritası sızdırmıyor: kullanıcı zaten hangi alanda olduğunu
   * biliyor. Sızdırmayan ama YANLIŞ olan bir metin, sızdırmayan ve doğru olanla
   * değiştirilebilir.
   */
  const enterpriseAlan =
    activeTenant !== null &&
    (ENTERPRISE_TENANT_TYPES as readonly string[]).includes(activeTenant.type);

  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: enterpriseAlan
            ? {
                title: "Audit kayıtlarını görme yetkiniz yok",
                description:
                  "Bu ekran ayrı bir yetki gerektirir; organizasyon yöneticinizle görüşün.",
              }
            : {
                title: "Audit kayıtları bu çalışma alanında tutulmuyor",
                description:
                  "Audit defteri yalnız kurumsal çalışma alanlarında tutulur. Kişisel alanınızın kendi kayıt defteri yoktur.",
              },
          error: {
            title: "Audit kayıtları yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  const satirlar = sorgu.data?.items ?? [];

  /**
   * DIŞA AKTARMA KAPSAMI — gösterilen cümle ile gönderilen değerler AYNI kaynaktan
   * (CLOS-194 `[172]` · alt görev CLOS-911).
   *
   * ⚠ Kartın kriteri: *"tüm organizasyon verisi export ediliyormuş izlenimi verilmez."*
   * Süzgeç varken bunu yazmasaydık kullanıcı, ekranda gördüğü daraltılmış listeyi değil
   * defterin tamamını indirdiğini sanabilirdi — ya da tersi.
   *
   * ⚠ `before`/`limit` GÖNDERİLMİYOR: sunucunun talep şeması ikisini dışarıda bırakıyor.
   * Sayfalama ekranın bakış açısı; dışa aktarma ise kapsamın TAMAMINI alıyor.
   */
  const exportFiltreleri: Record<string, string> = {};
  if (kategori) exportFiltreleri.category = kategori;
  if (aktor) exportFiltreleri.actorId = aktor.id;
  if (correlation) exportFiltreleri.correlationId = correlation;
  if (baslangic) exportFiltreleri.from = new Date(baslangic).toISOString();

  const kapsamParcalari: string[] = [];
  if (kategori) kapsamParcalari.push(`kategori "${kategori}"`);
  if (aktor) kapsamParcalari.push(`aktör ${aktor.ad}`);
  if (correlation) kapsamParcalari.push(`correlation ${correlation}`);
  if (baslangic) kapsamParcalari.push(`${baslangic} sonrası`);

  const kapsamMetni =
    kapsamParcalari.length > 0
      ? `Şu an seçili süzgeçler dışa aktarılır: ${kapsamParcalari.join(", ")}.`
      : "Süzgeç seçili değil: bu çalışma alanının tüm audit kayıtları dışa aktarılır.";

  function temizle() {
    setKategori("");
    setAktor(null);
    setCorrelation("");
    setBaslangic("");
  }

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title="Audit Kayıtları"
        description="Çalışma alanınızda kimin ne yaptığının değişmez kaydı. Kayıtlar düzenlenemez ve silinemez."
      />

      {/*
        ⚠ HATA VARKEN SÜZGEÇ ÇİZİLMİYOR.
        
        Canlı turda görüldü: yetkisi olmayan kullanıcıya 403 metninin ÜSTÜNDE çalışan
        görünümlü bir filtre paneli duruyordu. Kullanılamayan bir kontrolü çizmek,
        kullanıcıya denemesi için bir yol göstermek ve her denemesinde aynı duvara
        çarptırmak demek.
      */}
      {hataDurumu ? null : (
        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="secondary"
            className="w-fit"
            aria-expanded={suzgeclerAcik}
            onClick={() => setSuzgeclerAcik((acik) => !acik)}
          >
            Süzgeçler{ek ? " (uygulanıyor)" : ""}
          </Button>
          <Card className={suzgeclerAcik ? "block" : "hidden"}>
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Kategori">
                  <Input
                    value={kategori}
                    placeholder="contract, client, membership…"
                    onChange={(o) => setKategori(o.target.value)}
                  />
                </FormField>
                {/*
              ⚠ SALT OKUNUR ve satırdan besleniyor — elle UUID yazılan bir kutu değil.
              İlk hâli boş bir metin kutusuydu ve kullanıcının o kimliği nereden bulacağına
              dair hiçbir yol yoktu: beslenemeyen bir filtre, çalışıyormuş gibi durduğu
              için olmayan bir filtreden daha kötüdür.
            */}
                <FormField label="Aktör">
                  <Input
                    readOnly
                    data-testid="aktor-suzgeci"
                    value={aktor ? aktor.ad : ""}
                    placeholder="Bir satırdaki isme tıklayın"
                    onChange={() => undefined}
                  />
                </FormField>
                <FormField label="Correlation kimliği">
                  <Input value={correlation} onChange={(o) => setCorrelation(o.target.value)} />
                </FormField>
                <FormField label="Şu tarihten sonra">
                  <Input
                    type="date"
                    value={baslangic}
                    onChange={(o) => setBaslangic(o.target.value)}
                  />
                </FormField>
              </div>

              {ek ? (
                <div>
                  <Button size="sm" variant="secondary" onClick={temizle}>
                    Süzgeçleri temizle
                  </Button>
                </div>
              ) : null}
            </div>
          </Card>
        </div>
      )}

      {/*
        ⚠ Panel YETKİ VARSA çiziliyor ve hata varken HİÇ çizilmiyor.
        `organization.audit_export` ayrı bir anahtar; okuma yetkisiyle görünen bir düğme
        her tıklamada 403'e çarpardı. Süzgeç kartının aynı gerekçesi burada da geçerli.
      */}
      {hataDurumu || !activeTenantId || !can("organization.audit_export") ? null : (
        <AuditExportPanel
          tenantId={activeTenantId}
          filtreler={exportFiltreleri}
          kapsamMetni={kapsamMetni}
        />
      )}

      {/*
        ⚠ İÇGÖRÜ BÖLÜMÜ HATA VARKEN ÇİZİLMİYOR: audit listesi 403/hata verirken ondan
        çıkarılmış bir yorum düğmesi göstermek, kapalı kapının yanına çalışır görünen
        ikinci bir kol koymaktır. Süzgeç ve dışa aktarma panelinin gerekçesiyle aynı.
      */}
      {hataDurumu || !activeTenantId ? null : <AiOpsInsightSection />}

      {sorgu.isPending ? (
        <div role="status" aria-label="Audit kayıtları yükleniyor">
          <Skeleton lines={5} />
        </div>
      ) : hataDurumu ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : satirlar.length === 0 ? (
        <div className="rounded-lg border border-border-default p-4">
          <p className="text-sm text-fg-muted">
            {ek
              ? "Seçtiğiniz ölçütlere uyan kayıt yok."
              : "Bu çalışma alanında henüz audit kaydı yok."}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {satirlar.map((satir) => (
            <li key={satir.id}>
              <AuditSatiri
                satir={satir}
                locale={preferences.locale}
                acik={secili === satir.id}
                onSec={() => setSecili(secili === satir.id ? null : satir.id)}
                onAktoreSuz={setAktor}
              />
              {secili === satir.id ? (
                <AuditDetayi
                  yukleniyor={detay.isPending}
                  detay={detay.data ?? null}
                  locale={preferences.locale}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AuditSatiri({
  satir,
  locale,
  acik,
  onSec,
  onAktoreSuz,
}: {
  satir: AuditEntry;
  locale: string;
  acik: boolean;
  onSec: () => void;
  onAktoreSuz: (aktor: { id: string; ad: string }) => void;
}) {
  return (
    <Button
      variant="secondary"
      data-testid="audit-satiri"
      aria-expanded={acik}
      onClick={onSec}
      className="!h-auto w-full flex-wrap !justify-between gap-2 !text-left whitespace-normal"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium text-fg-strong">{satir.action}</span>
        <span className="truncate text-xs text-fg-soft">
          {satir.entity}
          {/*
            ⚠ Hedefe GİDİLEBİLİR Mİ bilgisi sunucudan. Erişim yoksa bağlantı çizilmiyor ve
            bunun SEBEBİ yazılıyor — sessizce kapatsaydık kullanıcı arayüzün bozuk olduğunu
            sanardı.
          */}
          {satir.targetAccessible ? null : (
            <span data-testid="hedef-kapali"> · hedefe erişiminiz yok</span>
          )}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/*
          ⚠ KİM YAPTI, sonra hangi sıfatla. Canlı turda ekran yalnız ROLÜ gösteriyordu ve
          iki yöneticili bir organizasyonda defter "kim yaptı" sorusuna cevap vermiyordu —
          bir kanıt defteri için işlevsiz bir hâl. Rol tarihsel (satırdan), ad kişinin
          bugünkü kimliği.
        */}
        <AktorEtiketi satir={satir} onSuz={onAktoreSuz} />
        {satir.actorRole ? <Badge tone="neutral">{satir.actorRole}</Badge> : null}
        <span className="text-xs text-fg-soft">{formatDateTime(satir.createdAt, locale)}</span>
      </div>
    </Button>
  );
}

/**
 * AKTÖR ETİKETİ.
 *
 * ### ⚠ "Aktör kimliği" filtre kutusu buradan BESLENİYOR
 *
 * Kutu ilk hâlinde ham bir UUID bekliyordu; kullanıcının o kimliği nereden bulacağına dair
 * hiçbir yol yoktu — beslenemeyen bir filtre, olmayan bir filtreden daha kötüdür çünkü
 * çalışıyormuş gibi durur. Ada tıklamak *"bu kişinin yaptığı her şey"* sorusunun kendisi.
 *
 * ### ⚠ Ad yoksa UYDURULMUYOR
 *
 * Sunucu `actorName: null` döndüyse hesap artık yok. "Bilinmeyen kullanıcı" yazmak, kaydın
 * kimliği kaybettiğini gizlemek olurdu; kısaltılmış kimlik gösteriliyor ve süzme yine
 * çalışıyor — defterin geri kalanı hâlâ o aktöre bağlanabilmeli.
 */
function AktorEtiketi({
  satir,
  onSuz,
}: {
  satir: AuditEntry;
  onSuz: (aktor: { id: string; ad: string }) => void;
}) {
  /** Sistem eylemlerinin kişisi yok; "kim" sorusu burada anlamsız. */
  if (satir.actorType !== "user" || !satir.actorId) {
    return (
      <span data-testid="aktor-sistem" className="text-xs text-fg-soft">
        Sistem
      </span>
    );
  }

  const ad = satir.actorName ?? `${satir.actorId.slice(0, 8)}…`;

  return (
    <span
      role="button"
      tabIndex={0}
      data-testid="aktor-adi"
      title={satir.actorName ? `${satir.actorName} eylemlerini süz` : "Bu aktörün eylemlerini süz"}
      className="cursor-pointer rounded px-1 text-xs font-medium text-fg-strong underline decoration-dotted hover:bg-muted"
      onClick={(o) => {
        /** ⚠ Satırın kendi tıklaması detayı açıyor; süzme onu tetiklememeli. */
        o.stopPropagation();
        onSuz({ id: satir.actorId!, ad });
      }}
      onKeyDown={(o) => {
        if (o.key !== "Enter" && o.key !== " ") return;
        o.preventDefault();
        o.stopPropagation();
        onSuz({ id: satir.actorId!, ad });
      }}
    >
      {ad}
      {satir.actorName ? null : (
        <span data-testid="aktor-adsiz" className="text-fg-soft">
          {" "}
          (hesap yok)
        </span>
      )}
    </span>
  );
}

function AuditDetayi({
  yukleniyor,
  detay,
  locale,
}: {
  yukleniyor: boolean;
  detay: AuditDetail | null;
  locale: string;
}) {
  if (yukleniyor) {
    return (
      <div role="status" aria-label="Audit detayı yükleniyor" className="px-3 py-2">
        <Skeleton lines={2} />
      </div>
    );
  }
  if (!detay) return null;

  return (
    <div
      data-testid="audit-detayi"
      className="mt-1 flex flex-col gap-3 rounded-md border border-border-default bg-muted/40 p-3"
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-fg-soft">Olay kimliği</dt>
        <dd className="font-mono text-xs text-fg">{detay.id}</dd>
        <dt className="text-fg-soft">Kaynak</dt>
        <dd className="text-fg">{detay.source}</dd>
        <dt className="text-fg-soft">Aktör türü</dt>
        <dd className="text-fg">{detay.actorType}</dd>
        <dt className="text-fg-soft">Tarih</dt>
        <dd className="text-fg">{formatDateTime(detay.createdAt, locale)}</dd>
        {detay.correlationId ? (
          <>
            <dt className="text-fg-soft">Correlation</dt>
            <dd className="font-mono text-xs text-fg">{detay.correlationId}</dd>
          </>
        ) : null}
        {detay.chainSeq !== null ? (
          <>
            <dt className="text-fg-soft">Zincir sırası</dt>
            <dd className="text-fg">{detay.chainSeq}</dd>
          </>
        ) : null}
      </dl>

      {/*
        ⚠ MASKELEME SESSİZ DEĞİL. Kullanıcı boş bir alan görüp "veri yoktu" sanmamalı;
        "burada bir şey var ama size gösterilmiyor" bilgisini almalı.
      */}
      {detay.masked ? (
        <p data-testid="maskeleme-uyarisi" className="text-xs text-fg-soft">
          Bu kayıtta gizlenen alanlar var: parola, token ve benzeri hassas veriler hiçbir görünümde
          açılmaz.
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <FarkKutusu baslik="Öncesi" veri={detay.before} />
        <FarkKutusu baslik="Sonrası" veri={detay.after} />
      </div>
    </div>
  );
}

function FarkKutusu({ baslik, veri }: { baslik: string; veri: unknown }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-strong">{baslik}</span>
      {veri === null || veri === undefined ? (
        <span className="text-xs text-fg-soft">—</span>
      ) : (
        <pre className="overflow-x-auto rounded bg-surface p-2 text-xs text-fg">
          {JSON.stringify(veri, null, 2)}
        </pre>
      )}
    </div>
  );
}
